import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import { ApolloClient, InMemoryCache, concat, DefaultOptions, ApolloLink, Observable, FetchResult } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';

import { typePolicies } from 'utils/apolloHelpers';
import { cognitoTokenManager } from 'utils/cognitoTokenManager';
import { resolveServiceToken } from 'utils/cognitoTokenStore';
import { forceLogoutToLogin } from 'utils/loginHelpers';
import { refreshAuthTokens } from './refreshTokens';

const uploadLink = createUploadLink({
    uri: process.env.REACT_APP_GRAPHQL_URL,
    credentials: 'include'
});

// Field names that require Cognito token (before backend cookie is set)
const PRE_LOGIN_FIELDS = ['getUserDetails', 'SaasUserLogin', 'sendMfaAttemptEmail', 'sendMfaSuccessEmail'];

// Helper to get the first field name from a GraphQL operation
const getFieldName = (operation: {
    query: { definitions: Array<{ selectionSet?: { selections: Array<{ name?: { value: string } }> } }> };
}) => {
    try {
        const firstDefinition = operation.query.definitions[0];
        if (firstDefinition?.selectionSet?.selections?.[0]?.name?.value) {
            return firstDefinition.selectionSet.selections[0].name.value;
        }
    } catch {
        return null;
    }
    return null;
};

const authLink = setContext(async (operation, { headers }) => {
    const tenant_id = localStorage.getItem('tenant_id') || undefined;

    // Generate unique nonce and timestamp for each request
    const nonce = uuidv4();
    const timestamp = Date.now().toString();

    // Only send Cognito token for pre-login operations (before backend_jwt cookie exists)
    // All other operations rely on the backend_jwt cookie sent via credentials: 'include'
    const fieldName = getFieldName(operation as unknown as Parameters<typeof getFieldName>[0]);
    const needsAuthHeader = fieldName ? PRE_LOGIN_FIELDS.includes(fieldName) : false;
    const authorization = needsAuthHeader ? (await resolveServiceToken()) ?? undefined : undefined;

    return {
        headers: {
            ...headers,
            tenant_id,
            ...(authorization && { authorization }),
            'X-Request-Nonce': nonce,
            'X-Request-Timestamp': timestamp
        }
    };
});

const MAX_AUTH_RETRIES = 1;

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
    const isAuthError = graphQLErrors?.some(
        (error) =>
            error.message?.includes('TokenExpiredError') ||
            error.extensions?.code === ('UNAUTHENTICATED' as string | undefined)
    );
    if (!isAuthError) return undefined;

    // The refresh mutation itself came back UNAUTHENTICATED — the backend has rejected the
    // refresh cookie (revoked, evicted, or otherwise unrecoverable). The previous behavior
    // here only showed a modal, which left in-flight queries free to re-enter this branch
    // and lock the user in an unrecoverable retry loop until they manually logged out.
    // Force a hard logout + redirect to /login so all in-memory auth state is torn down.
    if (getFieldName(operation as unknown as Parameters<typeof getFieldName>[0]) === 'refreshTokens') {
        cognitoTokenManager.clearTokens();
        forceLogoutToLogin();
        return undefined;
    }

    const authRetryCount = (operation.getContext().authRetryCount as number | undefined) ?? 0;
    if (authRetryCount >= MAX_AUTH_RETRIES) {
        // We already retried once and the query is still UNAUTHENTICATED — treat as terminal.
        cognitoTokenManager.clearTokens();
        forceLogoutToLogin();
        return undefined;
    }
    operation.setContext({ authRetryCount: authRetryCount + 1 });

    return new Observable<FetchResult>((observer) => {
        // Refresh both Cognito (in-memory) and backend (HttpOnly cookie) in parallel.
        Promise.allSettled([cognitoTokenManager.refreshTokens(), refreshAuthTokens()])
            .then(([, backendResult]) => {
                const backendOk = backendResult.status === 'fulfilled';
                // The backend_jwt cookie is the authoritative auth for every non-pre-login
                // op, and Cognito's refresh token in the browser typically outlives the
                // backend one — so a "both failed" check almost never triggers in
                // practice, leaving the user stuck after a backend revocation. If the
                // backend rotation failed, no forward(operation) retry will succeed:
                // force logout regardless of Cognito's state.
                if (!backendOk) {
                    cognitoTokenManager.clearTokens();
                    forceLogoutToLogin();
                    observer.error(new Error('Token refresh failed'));
                    return;
                }
                forward(operation).subscribe({
                    next: observer.next.bind(observer),
                    error: observer.error.bind(observer),
                    complete: observer.complete.bind(observer)
                });
            })
            .catch((err) => {
                cognitoTokenManager.clearTokens();
                forceLogoutToLogin();
                observer.error(err);
            });
    });
});

const defaultOptions: DefaultOptions = {
    query: {
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: true
    }
};

export const client = new ApolloClient({
    link: concat(authLink.concat(errorLink), uploadLink as unknown as ApolloLink),
    cache: new InMemoryCache({
        addTypename: false,
        typePolicies
    }),
    defaultOptions
});
